import { ReactNode } from 'react';
import classes from './styles.module.scss';

export interface ITableHeader {
	value: string;
	title?: string | JSX.Element;
	width?: string;
	formatted?: (row: any) => any;
	filter?: () => ReactNode;
	visible?: boolean | ((row: any) => boolean);
}

export class TableFilter {
	[key: string]: any;

	constructor(initialData: Record<string, any> = {}) {
		Object.assign(this, initialData);
	}
}

export interface ITableProps<T> {
	headers: ITableHeader[];
	rows: T[];
	isLoading?: boolean;
	getRowStyle?: (row: T) => React.CSSProperties;
}

export default function Table<T extends Record<string, any>>({
	headers,
	rows,
	isLoading = false,
	getRowStyle,
}: ITableProps<T>) {
	const visibleHeaders = headers.filter(
		header =>
			header.visible === undefined ||
			(typeof header.visible === 'function'
				? rows.some(row => (header.visible as (row: any) => boolean)(row))
				: header.visible),
	);

	// Генерируем уникальный ключ для строки
	// Приоритет: id > индекс (для гарантии уникальности)
	const getRowKey = (row: T, index: number) => {
		if (row.id !== undefined && row.id !== null) {
			return String(row.id);
		}
		return index;
	};

	return (
		<div className={classes['table-overlay']}>
			<table className={classes.table}>
				<thead>
					<tr>
						{visibleHeaders.map(header => (
							<th
								style={{
									...(header.width ? { width: header.width, maxWidth: header.width } : {}),
									 padding: '6px'
								}}
								key={`filter-${header.value}`}
							>
								<div>{header.filter?.()}</div>
							</th>
						))}
					</tr>

					<tr>
						{visibleHeaders.map(header => (
							<th
								style={{
									...(header.width ? { width: header.width, maxWidth: header.width } : {}),
								}}
								key={`title-${header.value}`}
							>
								<div
									style={{
										...(header.width
											? {
													wordWrap: 'break-word',
													overflowWrap: 'break-word',
													hyphens: 'auto',
											  }
											: {
													whiteSpace: 'nowrap',
											  }),
									}}
								>
									{header.title}
								</div>
							</th>
						))}
					</tr>
				</thead>

				{rows.length > 0 ? (
					<tbody>
						{rows.map((row, rowIndex) => (
							<tr key={getRowKey(row, rowIndex)} style={getRowStyle ? getRowStyle(row) : undefined}>
								{visibleHeaders.map(header => (
									<td
										key={`${header.value}-${row[header.value]}`}
										style={{
											...(header.width ? { width: header.width, maxWidth: header.width } : {}),
										}}
									>
										<div
											style={{
												...(header.width
													? {
															wordWrap: 'break-word',
															overflowWrap: 'break-word',
													  }
													: {
															whiteSpace: 'nowrap',
													  }),
											}}
										>
											{header.formatted ? header.formatted(row) : row[header.value]}
										</div>
									</td>
								))}
							</tr>
						))}
					</tbody>
				) : (
					<tbody>
						{isLoading ? (
							<div className={classes['loading-overlay']}>
								<div className={classes['loading-spinner']}>
									<div className={classes.spinner}></div>
									<span>Загрузка...</span>
								</div>
							</div>
						) : (
							<tr>
								<td colSpan={visibleHeaders.length} style={{ textAlign: 'center' }}>
									Записей не найдено.
								</td>
							</tr>
						)}
					</tbody>
				)}
			</table>
		</div>
	);
}
